import { Injectable, Inject, LOCALE_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from './http.service';
import { ApiEndpoints } from '../enums/api.enum';
import { ReviewFormsHeaders } from '../enums/dynamic-table-enums/review-forms-headers';
import { RoleTypeId } from '../enums/role-type.enum';
import { DatePipe } from '@angular/common';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { StatusMap } from '../enums/form-status.enum';

export interface DashboardData {
  totalUsers: number;
  totalSales: number;
  recentActivities: string[];
  chartData: any;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface ChartConfig {
  labelField: string;
  dataField: string;
}

export interface StatCardConfig {
  key: string;
  label: string;
  icon: string;
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private datePipe: DatePipe;
  constructor(
    private http: HttpService,
    @Inject(LOCALE_ID) private locale: string,
    private authService: AuthService,
    private toastService: ToastService
  ) {
    this.datePipe = new DatePipe(this.locale);
  }

  toLocalISOString(date: Date): string {
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().replace('Z', '');
  }

  private parseDateOnly(dateStr: string): Date | null {
    if (!dateStr) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    return new Date(year, month, day);
  }

  // Super Admin Dashboard Stats API's
  getDashboardStats(): Observable<any> {
    return this.http.post(ApiEndpoints.GET_DASHBOARD_STATS, {});
  }

  // Dashboard Stats API's
  getAdminDashboardStats(): Observable<any> {
    return this.http.post(ApiEndpoints.GET_ADMIN_DASHBOARD_STATS, {});
  }
  getManagerDashboardStats(): Observable<any> {
    return this.http.post(ApiEndpoints.GET_MANAGER_DASHBOARD_STATS, {});
  }
  getEmployeeDashboardStats(): Observable<any> {
    return this.http.post(ApiEndpoints.GET_EMPLOYEE_DASHBOARD_STATS, {});
  }

  // Dashboard Recent Activities API's
  getAdminRecentActivities(): Observable<any> {
    return this.http.post(ApiEndpoints.GET_ADMIN_RECENT_ACTIVITIES, { limit: 7 });
  }
  getManagerRecentActivities(): Observable<any> {
    return this.http.post(ApiEndpoints.GET_MANAGER_RECENT_ACTIVITIES, { limit: 7 });
  }
  getEmployeeRecentActivities(): Observable<any> {
    return this.http.post(ApiEndpoints.GET_EMPLOYEE_RECENT_ACTIVITIES, { limit: 7 });
  }

  isSuperAdmin(): boolean {
    try {
      // Super admin flag is stored as '/admin' in localStorage under key 'super_admin'
      return localStorage.getItem('super_admin') === '/admin';
    } catch {
      return false;
    }
  }

  getTenantStatsByInterval(data: any = {}): Observable<any> {
    return this.http.post(ApiEndpoints.GET_TENANT_STATS_BY_INTERVAL, data);
  }

  getRaisedFormByInterval(data: any = {}): Observable<any> {
    return this.http.post(ApiEndpoints.GET_RAISED_FORM_BY_INTERVAL, data);
  }

  getFormRecentActivities(data: any = {}): Observable<any> {
    return this.http.post(ApiEndpoints.GET_FORM_RECENT_ACTIVITIES, data);
  }

  getTenantRecentActivity(data: any = {}): Observable<any> {
    return this.http.post(ApiEndpoints.GET_TENANT_RECENT_ACTIVITY, data);
  }

  getFormApprovalComparison(data: any = {}): Observable<any> {
    return this.http.post(ApiEndpoints.GET_FORM_APPROVAL_COMPARISON, data);
  }

  getTenantComparison(data: any = {}): Observable<any> {
    return this.http.post(ApiEndpoints.GET_TENANT_COMPARISON, data);
  }

  getTenantLogo(): Observable<any> {
    return this.http.get(ApiEndpoints.GET_TENANT_LOGO);
  }

  updateTenantLogo(data: any = {}): Observable<any> {
    return this.http.post(ApiEndpoints.UPDATE_TENANT_LOGO, data);
  }

  // Returns start date (7 days ago) and end date (today)
  getLastWeekDateRange(): DateRange {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 6); // 6 days ago + today = 7 days total
    startDate.setHours(0, 0, 0, 0);

    return {
      startDate: this.toLocalISOString(startDate),
      endDate: this.toLocalISOString(endDate)
    };
  }

  // Returns start date (1 month ago) and end date (today)
  getLastMonthDateRange(): DateRange {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);

    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    startDate.setHours(0, 0, 0, 0);

    return {
      startDate: this.toLocalISOString(startDate),
      endDate: this.toLocalISOString(endDate)
    };
  }

  // Formats a date using DatePipe
  formatDate(date: any): string {
    if (!date) return '';
    return this.datePipe.transform(date, 'MMM dd, yyyy') || '';
  }

  // Converts date fields in data array to weekday names
  convertDatesToWeekdays(data: any[], labelField: string): void {
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    data.forEach(item => {
      if (item[labelField]) {
        const dateStr = item[labelField];
        let date: Date;
        if (typeof dateStr === 'string') {
          date = this.parseDateOnly(dateStr) || new Date(dateStr);
        } else {
          date = dateStr;
        }
        if (!isNaN(date.getTime())) {
          const dayOfWeek = date.getDay();
          item[labelField] = weekdays[dayOfWeek];
        }
      }
    });
  }

  // Transforms form data based on role type
  transformFormByRole(form: any, roleTypeId: number, currentUserId: number): any {
    const date = form['submitted_date'] || form['date_assigned'] || form['storedDate'] || form['created_at'] || form['updated_at'] || '';
    let departmentName = form['DepartmentForm']?.Department?.name || '';
    let roleName = form['role_name'] || '';

    if (!departmentName) {
      const UserDepartments = form['UserDepartments'] as Array<Record<string, unknown>>;
      if (UserDepartments && UserDepartments.length > 0) {
        departmentName = UserDepartments[0]?.['department_name'] as string || '';
        if (UserDepartments && UserDepartments.length > 1) {
          departmentName = UserDepartments.map((dept: Record<string, unknown>) => dept['department_name'] || '').filter(Boolean).join(', ');
        }
      }
    }
    if (!roleName) {
      const UserRoles = form['UserRoles'] as Array<Record<string, unknown>>;
      if (UserRoles && UserRoles.length > 0) {
        roleName = UserRoles[0]?.['role_name'] as string || '';
        if (UserRoles && UserRoles.length > 1) {
          roleName = UserRoles.map((role: Record<string, unknown>) => role['role_name'] || '').filter(Boolean).join(', ');
        }
      }
    }

    const departmentFormName = form['department_form_name'] ||
      (form['DepartmentForm'] as Record<string, unknown>)?.['name'] || '';

    const status = form['status'] !== undefined ? form['status'] : form['status_value'];
    let statusDisplay = '';
    if (status !== undefined && status !== null && status !== '') {
      const statusValue = typeof status === 'boolean' ? (status ? 1 : 0) :
        typeof status === 'string' ? (status === '1' || status.toLowerCase() === 'active' ? 1 : 0) :
          Number(status);
      statusDisplay = statusValue === 1 ? 'Active' : 'In Active';
    }
    const employeeDisplay = this.getEmployeeDisplay(form, currentUserId)

    const transformed: any = {
      ...form,
      tracking_id_display: form['tracking_id'] ? String(form['tracking_id']) : '',
      form_number_display: form['id'] ? String(form['id']) : '',
      employee_display: employeeDisplay,
      date_assigned_display: this.formatDate(date),
      department_name: departmentName,
      department_form_name: departmentFormName,
      status_display: form['status_value'] || form['status'] || StatusMap[form['status_id'] as number] || '',
      status_value: statusDisplay,
      role_name: roleName
    }

    return transformed;
  }

  private getEmployeeDisplay(form: Record<string, unknown>, currentUserId?: number): string {
    const user = form['User']
    const fullName =
      user && typeof user === 'object'
        ? (user as Record<string, unknown>)?.['full_name']
        : undefined
    if (typeof fullName === 'string' && fullName.trim()) {
      return fullName.trim()
    }

    const employeeName = form['employee_name']
    if (typeof employeeName === 'string' && employeeName.trim()) {
      return employeeName.trim()
    }

    const employee = form['employee']
    if (employee && typeof employee === 'object') {
      const name = (employee as Record<string, unknown>)?.['name']
      if (typeof name === 'string' && name.trim()) {
        return name.trim()
      }
    }
    if (typeof employee === 'string' && employee.trim()) {
      return employee.trim()
    }

    const userId = form['user_id'] ?? currentUserId
    if (userId != null && userId !== '') {
      return `User ${userId}`
    }

    return ''
  }

  // Transforms admin form data for super admin view
  transformAdminForm(form: Record<string, unknown>): Record<string, unknown> {
    return {
      ...form,
      tenant_name_display: form['tenant_name'] || '',
      user_name_display: form['user_name'] || '',
      user_email_display: form['user_email'] || '',
      plan_name_display: form['plan_name'] || '',
      updated_at_display: this.formatDate(form['updated_at']),
      renewal_date_display: form['renewal_date'] ? this.formatDate(form['renewal_date']) : 'N/A'
    };
  }

  // Detects label and data fields from chart data
  detectChartConfig(data: any[]): ChartConfig {
    if (data.length === 0) {
      return { labelField: '', dataField: '' };
    }

    const keys = Object.keys(data[0]);
    const labelFields = ['created_at', 'date', 'timestamp', 'time', 'period', 'label', 'name', 'title'];
    const dataFields = ['count', 'value', 'total', 'amount', 'sum', 'quantity', 'number'];

    return {
      labelField: keys.find(key =>
        labelFields.some(df => key.toLowerCase().includes(df))) || keys[0],
      dataField: keys.find(key =>
        dataFields.some(nf => key.toLowerCase().includes(nf))) || keys[1]
    };
  }

  // Processes form approval comparison data for chart display
  processFormApprovalComparisonData(data: any[], selectedYear1: number, selectedYear2: number): {
    chartData: any[];
    datasets: any[];
    config: ChartConfig;
  } {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const yearData: { [key: number]: { [key: string]: number } } = {
      [selectedYear1]: Object.fromEntries(months.map(m => [m, 0])),
      [selectedYear2]: Object.fromEntries(months.map(m => [m, 0]))
    };

    data.forEach(({ period, data: yearDataArray }: any) => {
      const year = parseInt(period, 10);
      if (!yearData[year]) return;
      yearDataArray.forEach(({ created_at, count }: any) => {
        const monthIndex = parseInt(created_at.split('-')[1], 10) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          yearData[year][months[monthIndex]] = Number(count) || 0;
        }
      });
    });

    const year1Values = months.map(m => yearData[selectedYear1][m]);
    const year2Values = months.map(m => yearData[selectedYear2][m]);

    const chartData = months.map(month => ({
      month,
      year1: yearData[selectedYear1][month],
      year2: yearData[selectedYear2][month]
    }));

    const createDataset = (label: string, data: number[], color: string) => ({
      label,
      data: [...data],
      borderColor: color,
      backgroundColor: color.replace('1)', '0.2)'),
      borderWidth: 3,
      fill: false,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: color,
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    });

    const datasets = [
      createDataset(String(selectedYear1), year1Values, 'rgba(255, 159, 64, 1)'),
      createDataset(String(selectedYear2), year2Values, 'rgba(34, 197, 94, 1)')
    ];

    return {
      chartData,
      datasets,
      config: { labelField: 'month', dataField: 'year1' }
    };
  }

  // Returns the appropriate service method based on role_type_id
  getRoleBasedServiceCall(roleTypeId: number): Observable<any> | null {
    if (roleTypeId === RoleTypeId.Admin) {
      return this.getAdminDashboardStats();
    }
    //  else if (roleTypeId === RoleTypeId.Employee) {
    //   return this.getManagerDashboardStats();
    // }
    else if (roleTypeId === RoleTypeId.Employee) {
      return this.getEmployeeDashboardStats();
    }
    return null;
  }

  // Returns the appropriate recent activities service method based on role_type_id
  getRoleBasedRecentActivitiesCall(roleTypeId: number): Observable<any> | null {
    if (roleTypeId === 2) {
      return this.getAdminRecentActivities();
    }
    // else if (roleTypeId === 1) {
    //   return this.getManagerRecentActivities();
    // } 
    else if (roleTypeId === 1) {
      return this.getEmployeeRecentActivities();
    }
    return null;
  }

  // Sets table header based on role type ID
  getTableHeaderByRole(roleTypeId: number): { key: string; label: string }[] {
    if (roleTypeId === RoleTypeId.Admin) {
      return [...ReviewFormsHeaders.adminHeader];
      // } else if (roleTypeId === RoleTypeId.Employee) {
      //   return [...ReviewFormsHeaders.managerHeader];
    } else if (roleTypeId === RoleTypeId.Employee) {
      return [...ReviewFormsHeaders.employeeHeader];
    } else {
      return [...ReviewFormsHeaders.adminHeader];
    }
  }

  // Updates stat cards configuration based on current role
  getStatCardsConfig(isSuperAdmin: boolean, roleTypeId: number): StatCardConfig[] {
    if (isSuperAdmin) {
      return [
        { key: 'totalTenants', label: 'Total Tenants', icon: 'pi-building' },
        { key: 'newTenantsThisMonth', label: 'New Tenants (This Month)', icon: 'pi-plus-circle' },
        { key: 'activeTenants', label: 'Active Tenants', icon: 'pi-check-circle' },
        { key: 'inactiveTenants', label: 'Inactive Tenants', icon: 'pi-pause-circle' },
        { key: 'suspendedTenants', label: 'Suspended Tenants', icon: 'pi-times-circle' },
      ];
    }

    if (roleTypeId === RoleTypeId.Admin) {
      return [
        { key: 'total_users', label: 'Total Users', icon: 'pi-users' },
        { key: 'total_departments', label: 'Total Departments', icon: 'pi-building' },
        { key: 'total_templates', label: 'Total Templates', icon: 'pi-file' },
        { key: 'total_forms', label: 'Total Forms', icon: 'pi-file-edit' },
      ];
      // } else if (roleTypeId === RoleTypeId.Employee) {
      //   return [
      //     { key: 'form_in_review', label: 'Forms In Review', icon: 'pi-clock' },
      //     { key: 'form_approved', label: 'Forms Approved', icon: 'pi-check-circle' },
      //     { key: 'form_rejected', label: 'Forms Rejected', icon: 'pi-times-circle' },
      //     { key: 'form_raised', label: 'Forms Raised', icon: 'pi-file-edit' },
      //   ];
    } else if (roleTypeId === RoleTypeId.Employee) {
      return [
        { key: 'form_in_review', label: 'Forms In Review', icon: 'pi-clock' },
        { key: 'form_approved', label: 'Forms Approved', icon: 'pi-check-circle' },
        { key: 'form_rejected', label: 'Forms Rejected', icon: 'pi-times-circle' },
        { key: 'form_in_draft', label: 'Forms In Draft', icon: 'pi-file' }
      ];
    } else {
      return [];
    }
  }

  // Maps API response data to dashboard stats based on role_type_id
  mapRoleBasedStats(roleTypeId: number, data: any): any {
    const defaultStats = {
      totalTenants: 0,
      newTenantsThisMonth: 0,
      activeTenants: 0,
      inactiveTenants: 0,
      suspendedTenants: 0,
      total_users: 0,
      total_departments: 0,
      total_templates: 0,
      total_forms: 0,
      form_in_review: 0,
      forms_in_review: 0,
      form_approved: 0,
      forms_approved: 0,
      form_rejected: 0,
      forms_rejected: 0,
      form_raised: 0,
      form_in_draft: 0,
    };

    if (roleTypeId === 2) {
      return {
        ...defaultStats,
        total_users: data.total_users ?? 0,
        total_departments: data.total_departments ?? 0,
        total_templates: data.total_templates ?? 0,
        total_forms: data.total_forms ?? 0,
      };
    }
    // else if (roleTypeId === 1) {
    //   return {
    //     ...defaultStats,
    //     forms_in_review: data.forms_in_review ?? 0,
    //     forms_approved: data.forms_approved ?? 0,
    //     forms_rejected: data.forms_rejected ?? 0,
    //     form_raised: data.form_raised ?? 0,
    //   };
    // } 
    else if (roleTypeId === RoleTypeId.Employee) {
      return {
        ...defaultStats,
        form_in_review: data.form_in_review ?? 0,
        form_approved: data.form_approved ?? 0,
        form_rejected: data.form_rejected ?? 0,
        form_in_draft: data.form_in_draft ?? 0
      };
    } else {
      return defaultStats;
    }
  }

  // Gets selected role from localStorage
  getSelectedRole(): any {
    const storedRole = localStorage.getItem('selectedRole');
    if (storedRole) {
      return JSON.parse(storedRole);
    }
    // Fallback to first role from user data
    const user = localStorage.getItem('user');
    if (user) {
      const parsedUser = JSON.parse(user);
      return parsedUser?.roles?.[0] || null;
    }
    return null;
  }

  getThemeVariation(hex: string, isDark: boolean): string {
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    const toHex = (v: number) => Math.min(255, Math.max(0, v)).toString(16).padStart(2, '0');
    if (isDark) {
      return `#${toHex(Math.round(r * 0.7))}${toHex(Math.round(g * 0.7))}${toHex(Math.round(b * 0.7))}`;
    } else {
      return `#${toHex(Math.round(r * 0.5 + 255 * 0.5))}${toHex(Math.round(g * 0.5 + 255 * 0.5))}${toHex(Math.round(b * 0.5 + 255 * 0.5))}`;
    }
  }

  updateManagerStatus(payload: { is_out_of_office: boolean }): void {
    this.http.post(ApiEndpoints.UPDATE_MANAGER_STATUS, payload).subscribe({
      next: () => {

        this.authService.setStatus(false);
      },
      error: (err) => {
        console.error('Error updating manager status:', err);
        this.toastService.showToast({ type: 'error', summary: 'Error', detail: 'Failed to update status. Please try again.' });
        this.authService.setStatus(false);
      }
    });
  }
}